import { create } from 'zustand';
import { regionService } from '../services/regionService';
import type { AreaNode, RegionTree, SubRegionNode } from '../types/region';

/**
 * areaId から解決される構成情報（所属の大地区・サブリージョン・地区）
 */
export interface AreaResolution {
  region: RegionTree;
  subRegion: SubRegionNode;
  area: AreaNode;
}

interface GeographyStore {
  /** 地理マスタツリー（Region → SubRegion → Area） */
  tree: RegionTree[];
  /** areaId → 構成情報 の索引 */
  areaIndex: Map<number, AreaResolution>;
  loading: boolean;
  /** 取得済みフラグ（再取得を抑制する） */
  loaded: boolean;
  error: string | null;

  /** 地理マスタツリーを取得する（取得済みなら何もしない） */
  fetchTree: () => Promise<void>;
  /**
   * areaId から所属の構成情報を解決する。
   * @param areaId - 地区 ID（数値または文字列）
   * @returns 構成情報。未登録の areaId なら undefined
   */
  resolveByAreaId: (areaId: number | string) => AreaResolution | undefined;
  reset: () => void;
}

/**
 * 取得中の fetchTree Promise。並走呼び出し（例: FieldDetailPage の effect と
 * fieldStore.fetchFields）で同一の取得を共有し、二重リクエストと「取得中に空ツリーを
 * 読んでしまう」競合を防ぐ。
 */
let inFlightFetch: Promise<void> | null = null;

/**
 * areaId 索引を構築する。
 * @param tree - 地理マスタツリー
 * @returns areaId → 構成情報 の Map
 */
function buildAreaIndex(tree: RegionTree[]): Map<number, AreaResolution> {
  const index = new Map<number, AreaResolution>();
  for (const region of tree) {
    for (const subRegion of region.subRegions) {
      for (const area of subRegion.areas) {
        index.set(area.id, { region, subRegion, area });
      }
    }
  }
  return index;
}

/**
 * 地理マスタ（構成情報）を保持する Zustand ストア。
 *
 * ロールに依らず全ユーザーが参照でき、圃場の areaId から所属 SubRegion（JA）を解決する。
 */
export const useGeographyStore = create<GeographyStore>((set, get) => ({
  tree: [],
  areaIndex: new Map(),
  loading: false,
  loaded: false,
  error: null,

  fetchTree: async () => {
    if (get().loaded) return;
    // 取得中なら同一 Promise を共有して待つ（並走呼び出しの競合・二重取得を防ぐ）
    if (inFlightFetch) return inFlightFetch;
    set({ loading: true, error: null });
    inFlightFetch = (async () => {
      try {
        const tree = await regionService.getRegionTree();
        set({ tree, areaIndex: buildAreaIndex(tree), loaded: true, loading: false });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '地理マスタの取得に失敗しました',
          loading: false,
        });
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  resolveByAreaId: (areaId) => {
    return get().areaIndex.get(Number(areaId));
  },

  reset: () => {
    inFlightFetch = null;
    set({ tree: [], areaIndex: new Map(), loading: false, loaded: false, error: null });
  },
}));
